// =============================================================================
// ACT MATH QUESTION BANK
// Form 2176CPRE - Complete 60 Questions
// =============================================================================
// 
// CATEGORIES (from official ACT):
//   PHM = Preparing for Higher Math (subcategories below)
//     N = Number & Quantity
//     A = Algebra
//     F = Functions
//     G = Geometry
//     S = Statistics & Probability
//   IES = Integrating Essential Skills
//   MDL = Modeling (overlay category)
//
// =============================================================================

export const ACT_CATEGORIES = {
  N: { name: 'Number & Quantity', color: '#8b5cf6', parent: 'PHM' },
  A: { name: 'Algebra', color: '#3b82f6', parent: 'PHM' },
  F: { name: 'Functions', color: '#06b6d4', parent: 'PHM' },
  G: { name: 'Geometry', color: '#10b981', parent: 'PHM' },
  S: { name: 'Statistics & Probability', color: '#f59e0b', parent: 'PHM' },
  IES: { name: 'Integrating Essential Skills', color: '#ef4444', parent: null },
  MDL: { name: 'Modeling', color: '#6366f1', parent: null, isOverlay: true }
};

// Official ACT Scale Score Conversion (Form 2176CPRE)
export const SCALE_SCORE_TABLE = [
  { min: 58, max: 60, scale: 36 },
  { min: 56, max: 57, scale: 35 },
  { min: 54, max: 55, scale: 34 },
  { min: 53, max: 53, scale: 33 },
  { min: 51, max: 52, scale: 32 },
  { min: 49, max: 50, scale: 31 },
  { min: 48, max: 48, scale: 30 },
  { min: 46, max: 47, scale: 29 },
  { min: 44, max: 45, scale: 28 },
  { min: 41, max: 43, scale: 27 },
  { min: 39, max: 40, scale: 26 },
  { min: 37, max: 38, scale: 25 },
  { min: 35, max: 36, scale: 24 },
  { min: 33, max: 34, scale: 23 },
  { min: 31, max: 32, scale: 22 },
  { min: 30, max: 30, scale: 21 },
  { min: 28, max: 29, scale: 20 },
  { min: 26, max: 27, scale: 19 },
  { min: 24, max: 25, scale: 18 },
  { min: 21, max: 23, scale: 17 },
  { min: 17, max: 20, scale: 16 },
  { min: 13, max: 16, scale: 15 },
  { min: 10, max: 12, scale: 14 },
  { min: 8, max: 9, scale: 13 },
  { min: 6, max: 7, scale: 12 },
  { min: 5, max: 5, scale: 11 },
  { min: 4, max: 4, scale: 10 },
  { min: 3, max: 3, scale: 8 },
  { min: 2, max: 2, scale: 6 },
  { min: 1, max: 1, scale: 4 },
  { min: 0, max: 0, scale: 1 }
];

export function getScaleScore(rawScore) {
  for (const row of SCALE_SCORE_TABLE) {
    if (rawScore >= row.min && rawScore <= row.max) {
      return row.scale;
    }
  }
  return 1;
}

// =============================================================================
// COMPLETE QUESTION BANK - FORM 2176CPRE
// =============================================================================

export const questions_2176CPRE = [
  // -------------------------------------------------------------------------
  // QUESTION 1
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-01',
    formId: '2176CPRE',
    questionNumber: 1,
    categories: ['S', 'IES', 'MDL'],
    difficulty: 1,
    question: 'The numbers 1 through 15 were each written on individual pieces of paper, 1 number per piece. Then the 15 pieces of paper were put in a jar. One piece of paper will be drawn from the jar at random. What is the probability of drawing a piece of paper with a number less than 9 written on it?',
    choices: ['1/9', '1/15', '6/15', '7/15', '8/15'],
    correctIndex: 4, // E
    correctLetter: 'E',
    explanation: 'Numbers less than 9: 1, 2, 3, 4, 5, 6, 7, 8 (that\'s 8 numbers). Total: 15. Probability = 8/15.',
    concepts: ['basic probability', 'counting'],
    templateParams: { total: 15, threshold: 9, condition: 'less than' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 2
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-02',
    formId: '2176CPRE',
    questionNumber: 2,
    categories: ['A'],
    difficulty: 1,
    question: 'Which of the following expressions is equivalent to −4x³ − 12x³ + 9x²?',
    choices: ['x⁸', '−7x⁸', '−8x³ + 9x²', '−16x³ + 9x²', '−16x⁶ + 9x²'],
    correctIndex: 3, // J
    correctLetter: 'J',
    explanation: 'Combine like terms: −4x³ − 12x³ = −16x³. The 9x² term has a different exponent so it stays separate. Result: −16x³ + 9x².',
    concepts: ['combining like terms', 'polynomial simplification'],
    templateParams: { coef1: -4, coef2: -12, exp1: 3, coef3: 9, exp2: 2 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 3
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-03',
    formId: '2176CPRE',
    questionNumber: 3,
    categories: ['IES'],
    difficulty: 1,
    question: 'When x = 2, 10 + 3(12 ÷ (3x)) = ?',
    choices: ['12', '16', '26', '34', '104'],
    correctIndex: 1, // B
    correctLetter: 'B',
    explanation: 'Substitute x = 2: 10 + 3(12 ÷ (3·2)) = 10 + 3(12 ÷ 6) = 10 + 3(2) = 10 + 6 = 16.',
    concepts: ['substitution', 'order of operations'],
    templateParams: { x: 2, expression: '10 + 3(12 ÷ (3x))' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 4
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-04',
    formId: '2176CPRE',
    questionNumber: 4,
    categories: ['IES'],
    difficulty: 1,
    question: '|6 − 4| − |3 − 8| = ?',
    choices: ['−7', '−3', '3', '7', '21'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: '|6 − 4| = |2| = 2. |3 − 8| = |−5| = 5. Result: 2 − 5 = −3.',
    concepts: ['absolute value', 'arithmetic'],
    templateParams: { expr1: [6, 4], expr2: [3, 8] }
  },

  // -------------------------------------------------------------------------
  // QUESTION 5
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-05',
    formId: '2176CPRE',
    questionNumber: 5,
    categories: ['A'],
    difficulty: 1,
    question: 'The expression (4c − 3d)(3c + d) is equivalent to:',
    choices: ['12c² − 13cd − 3d²', '12c² − 13cd + 3d²', '12c² − 5cd − 3d²', '12c² − 5cd + 3d²', '12c² − 3d²'],
    correctIndex: 2, // C
    correctLetter: 'C',
    explanation: 'FOIL: (4c)(3c) + (4c)(d) + (−3d)(3c) + (−3d)(d) = 12c² + 4cd − 9cd − 3d² = 12c² − 5cd − 3d².',
    concepts: ['FOIL', 'polynomial multiplication', 'binomial expansion'],
    templateParams: { a: 4, b: -3, c: 3, d: 1 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 6
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-06',
    formId: '2176CPRE',
    questionNumber: 6,
    categories: ['IES', 'MDL'],
    difficulty: 1,
    question: 'Of the 180 students in a college course, 1/4 of the students earned an A for the course, 1/3 of the students earned a B for the course, and the rest of the students earned a C for the course. How many of the students earned a C for the course?',
    choices: ['75', '90', '105', '120', '135'],
    correctIndex: 0, // F
    correctLetter: 'F',
    explanation: 'A students: 180 × 1/4 = 45. B students: 180 × 1/3 = 60. C students: 180 − 45 − 60 = 75.',
    concepts: ['fractions of whole', 'word problems'],
    templateParams: { total: 180, fractions: ['1/4', '1/3'] }
  },

  // -------------------------------------------------------------------------
  // QUESTION 7
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-07',
    formId: '2176CPRE',
    questionNumber: 7,
    categories: ['F', 'MDL'],
    difficulty: 2,
    question: 'The number of fish, f, in Skipper\'s Pond at the beginning of each year can be modeled by the equation f(x) = 3(2ˣ), where x represents the number of years after the beginning of the year 2000. For example, x = 0 represents the beginning of the year 2000, x = 1 represents the beginning of the year 2001, and so forth. According to the model, how many fish were in Skipper\'s Pond at the beginning of the year 2006?',
    choices: ['96', '192', '384', '1,458', '46,656'],
    correctIndex: 1, // B
    correctLetter: 'B',
    explanation: 'At 2006, x = 6 (years after 2000). f(6) = 3(2⁶) = 3(64) = 192.',
    concepts: ['exponential functions', 'function evaluation'],
    templateParams: { coefficient: 3, base: 2, startYear: 2000, targetYear: 2006 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 8
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-08',
    formId: '2176CPRE',
    questionNumber: 8,
    categories: ['IES', 'MDL'],
    difficulty: 1,
    question: 'Manish drove from Chicago to Baton Rouge. At 8:00 a.m., he was 510 km from Baton Rouge. At 1:00 p.m., he was 105 km from Baton Rouge. Which of the following values is closest to Manish\'s average speed, in kilometers per hour, from 8:00 a.m. to 1:00 p.m.?',
    choices: ['58', '68', '81', '94', '102'],
    correctIndex: 2, // H
    correctLetter: 'H',
    explanation: 'Distance traveled = 510 − 105 = 405 km. Time = 5 hours (8am to 1pm). Average speed = 405/5 = 81 km/h.',
    concepts: ['average speed', 'distance-rate-time'],
    templateParams: { startDist: 510, endDist: 105, startTime: '8:00', endTime: '13:00' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 9
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-09',
    formId: '2176CPRE',
    questionNumber: 9,
    categories: ['G'],
    difficulty: 2,
    question: 'In the figure shown below, E and G lie on AC, D and F lie on AB, DE and FG are parallel to BC, and the given lengths are in feet. AD = 8, DF = 7, FB = 6, AE = 16. What is the length of AC, in feet?',
    choices: ['13', '26', '29', '42', '48'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'By similar triangles with parallel lines: AE/AC = AD/AB. AB = AD + DF + FB = 8 + 7 + 6 = 21. So 16/AC = 8/21. AC = 16 × 21/8 = 42 feet.',
    concepts: ['similar triangles', 'parallel lines', 'proportions'],
    templateParams: { AD: 8, DF: 7, FB: 6, AE: 16 },
    hasFigure: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 10
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-10',
    formId: '2176CPRE',
    questionNumber: 10,
    categories: ['IES', 'MDL'],
    difficulty: 1,
    question: 'Katerina runs 15 miles in 2½ hours. What is the average number of minutes it takes her to run 1 mile?',
    choices: ['6', '10', '12½', '16⅔', '17½'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: '2.5 hours = 150 minutes. Time per mile = 150 minutes ÷ 15 miles = 10 minutes per mile.',
    concepts: ['unit rate', 'unit conversion'],
    templateParams: { distance: 15, time: 2.5, timeUnit: 'hours' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 11
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-11',
    formId: '2176CPRE',
    questionNumber: 11,
    categories: ['S', 'MDL'],
    difficulty: 2,
    question: 'A bag contains 8 red marbles, 9 yellow marbles, and 7 green marbles. How many additional red marbles must be added to the 24 marbles already in the bag so that the probability of randomly drawing a red marble is 3/5?',
    choices: ['11', '16', '20', '24', '32'],
    correctIndex: 1, // B
    correctLetter: 'B',
    explanation: 'Let x = additional red marbles. (8+x)/(24+x) = 3/5. Cross multiply: 5(8+x) = 3(24+x). 40 + 5x = 72 + 3x. 2x = 32. x = 16.',
    concepts: ['probability', 'algebraic equations'],
    templateParams: { red: 8, yellow: 9, green: 7, targetProb: '3/5' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 12
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-12',
    formId: '2176CPRE',
    questionNumber: 12,
    categories: ['G', 'MDL'],
    difficulty: 2,
    question: 'In the standard (x,y) coordinate plane, the point (2,1) is the midpoint of CD. Point C has coordinates (6,8). What are the coordinates of point D?',
    choices: ['(−2, −7/2)', '(−2, −6)', '(4, 9/2)', '(10, 10)', '(10, 15)'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: 'Midpoint formula: ((x₁+x₂)/2, (y₁+y₂)/2) = (2,1). So (6+x)/2 = 2 → x = −2. (8+y)/2 = 1 → y = −6. Point D = (−2, −6).',
    concepts: ['midpoint formula', 'coordinate geometry'],
    templateParams: { midpoint: [2, 1], pointC: [6, 8] }
  },

  // -------------------------------------------------------------------------
  // QUESTION 13
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-13',
    formId: '2176CPRE',
    questionNumber: 13,
    categories: ['IES', 'MDL'],
    difficulty: 2,
    question: 'At his job, the first 40 hours of each week that Thomas works is regular time, and any additional time that he works is overtime. Thomas gets paid $15 per hour during regular time. During overtime Thomas gets paid 1.5 times as much as he gets paid during regular time. Thomas works 46 hours in 1 week and gets $117 in deductions taken out of his pay for this week. After the deductions are taken out, how much of Thomas\'s pay for this week remains?',
    choices: ['$492', '$573', '$609', '$618', '$735'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'Regular pay: 40 × $15 = $600. Overtime rate: $15 × 1.5 = $22.50. Overtime pay: 6 × $22.50 = $135. Total before deductions: $735. After deductions: $735 − $117 = $618.',
    concepts: ['wages', 'overtime pay', 'deductions'],
    templateParams: { regularHours: 40, regularRate: 15, overtimeMultiplier: 1.5, hoursWorked: 46, deductions: 117 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 14
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-14',
    formId: '2176CPRE',
    questionNumber: 14,
    categories: ['IES', 'MDL'],
    difficulty: 2,
    question: 'At Sweet Stuff Fresh Produce the price of a bag of grapes depends on the total number of bags purchased at 1 time, as shown in the table below. In 2 trips to Sweet Stuff this week, Janelle purchased 3 bags of grapes on Monday and 4 bags of grapes on Wednesday. How much money would Janelle have saved if she had instead purchased 7 bags of grapes in 1 trip on Monday?\n\nNumber of bags | Price per bag\n1−3: $3.00\n4−6: $2.80\n7−9: $2.60\n10 or more: $2.50',
    choices: ['$0.20', '$1.00', '$1.40', '$2.00', '$2.50'],
    correctIndex: 3, // J
    correctLetter: 'J',
    explanation: 'Separate trips: (3 × $3.00) + (4 × $2.80) = $9.00 + $11.20 = $20.20. One trip: 7 × $2.60 = $18.20. Savings: $20.20 − $18.20 = $2.00.',
    concepts: ['tiered pricing', 'comparison'],
    templateParams: { tiers: [[1,3,3.00], [4,6,2.80], [7,9,2.60], [10,999,2.50]], purchases: [3, 4] }
  },

  // -------------------------------------------------------------------------
  // QUESTION 15
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-15',
    formId: '2176CPRE',
    questionNumber: 15,
    categories: ['N'],
    difficulty: 1,
    question: 'What is 3% of 4.14 × 10⁴?',
    choices: ['1,242', '1,380', '12,420', '13,800', '124,200'],
    correctIndex: 0, // A
    correctLetter: 'A',
    explanation: '4.14 × 10⁴ = 41,400. 3% of 41,400 = 0.03 × 41,400 = 1,242.',
    concepts: ['percentages', 'scientific notation'],
    templateParams: { percent: 3, coefficient: 4.14, exponent: 4 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 16
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-16',
    formId: '2176CPRE',
    questionNumber: 16,
    categories: ['IES'],
    difficulty: 2,
    question: 'The total cost of renting a car is $30.00 for each day the car is rented plus $0.15 for each mile the car is driven. What is the total cost of renting the car for 2 days and driving 340 miles?',
    choices: ['$51.00', '$81.00', '$91.00', '$101.00', '$111.00'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'Daily cost: 2 × $30 = $60. Mileage cost: 340 × $0.15 = $51. Total: $60 + $51 = $111.',
    concepts: ['linear cost function', 'two-part pricing'],
    templateParams: { dailyRate: 30, mileageRate: 0.15, days: 2, miles: 340 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 17
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-17',
    formId: '2176CPRE',
    questionNumber: 17,
    categories: ['IES'],
    difficulty: 1,
    question: 'For the school play, adult tickets cost $4 each and student tickets cost $2 each. Natalie sells 20 tickets and collects a total of $56. How many adult tickets did Natalie sell?',
    choices: ['6', '8', '10', '14', '16'],
    correctIndex: 1, // D
    correctLetter: 'D',
    explanation: 'Let a = adult tickets. Then student tickets = 20 − a. Total: 4a + 2(20−a) = 56. 4a + 40 − 2a = 56. 2a = 16. a = 8.',
    concepts: ['systems of equations', 'word problems'],
    templateParams: { adultPrice: 4, studentPrice: 2, totalTickets: 20, totalMoney: 56 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 18
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-18',
    formId: '2176CPRE',
    questionNumber: 18,
    categories: ['A'],
    difficulty: 2,
    question: 'If |2a − 3| = 5 and a < 0, then a = ?',
    choices: ['−4', '−1', '1', '2', '4'],
    correctIndex: 1, // J
    correctLetter: 'J',
    explanation: '|2a − 3| = 5 means 2a − 3 = 5 or 2a − 3 = −5. First: 2a = 8, a = 4. Second: 2a = −2, a = −1. Since a < 0, a = −1.',
    concepts: ['absolute value equations', 'constraints'],
    templateParams: { coefficient: 2, constant: -3, result: 5, constraint: 'negative' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 19
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-19',
    formId: '2176CPRE',
    questionNumber: 19,
    categories: ['F', 'MDL'],
    difficulty: 2,
    question: 'A company models its daily production cost, in dollars, using the function C(x) = 5x² + 200 where x is the number of items produced. What is the daily production cost, in dollars, when 10 items are produced?',
    choices: ['$700', '$750', '$1,200', '$2,700', '$5,200'],
    correctIndex: 0, // A
    correctLetter: 'A',
    explanation: 'C(10) = 5(10)² + 200 = 5(100) + 200 = 500 + 200 = $700.',
    concepts: ['function evaluation', 'quadratic functions'],
    templateParams: { a: 5, b: 0, c: 200, input: 10 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 20
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-20',
    formId: '2176CPRE',
    questionNumber: 20,
    categories: ['IES'],
    difficulty: 2,
    question: 'What value of x satisfies the equation (2/3)x − 4 = (1/6)x + 2?',
    choices: ['6', '12', '18', '24', '36'],
    correctIndex: 1, // F (but numbered, so index 1 = second choice)
    correctLetter: 'F',
    explanation: 'Multiply by 6 to clear fractions: 4x − 24 = x + 12. 3x = 36. x = 12.',
    concepts: ['linear equations', 'fractions'],
    templateParams: { leftCoef: '2/3', leftConst: -4, rightCoef: '1/6', rightConst: 2 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 21
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-21',
    formId: '2176CPRE',
    questionNumber: 21,
    categories: ['A'],
    difficulty: 2,
    question: 'If (x + a)² = x² + 16x + a², for all x, then a = ?',
    choices: ['4', '8', '16', '32', '64'],
    correctIndex: 1, // E (index 1 in 0-indexed)
    correctLetter: 'E',
    explanation: 'Expand (x + a)² = x² + 2ax + a². Comparing to x² + 16x + a², the middle terms must be equal: 2a = 16, so a = 8.',
    concepts: ['perfect square trinomial', 'algebraic identity'],
    templateParams: { middleCoefficient: 16 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 22
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-22',
    formId: '2176CPRE',
    questionNumber: 22,
    categories: ['A'],
    difficulty: 2,
    question: 'What is the sum of the 2 solutions of the equation x² + 2x − 35 = 0?',
    choices: ['−7', '−2', '2', '5', '7'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: 'Factor: (x + 7)(x − 5) = 0. Solutions: x = −7, x = 5. Sum: −7 + 5 = −2. (Or use Vieta: sum = −b/a = −2/1 = −2.)',
    concepts: ['quadratic equations', 'factoring', 'Vieta\'s formulas'],
    templateParams: { a: 1, b: 2, c: -35 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 23
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-23',
    formId: '2176CPRE',
    questionNumber: 23,
    categories: ['A'],
    difficulty: 2,
    question: 'For the line with equation y = mx + 8, the value of y decreases by 0.4 units for every increase of 2 units in x. What is the value of m?',
    choices: ['−0.8', '−0.2', '0.2', '0.8', '1.6'],
    correctIndex: 1, // E
    correctLetter: 'E',
    explanation: 'Slope m = rise/run = change in y / change in x = −0.4/2 = −0.2.',
    concepts: ['slope', 'linear equations'],
    templateParams: { yChange: -0.4, xChange: 2, yIntercept: 8 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 24
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-24',
    formId: '2176CPRE',
    questionNumber: 24,
    categories: ['N'],
    difficulty: 2,
    question: 'The set of all positive integers that are divisible by both 15 and 35 is infinite. What is the least positive integer in this set?',
    choices: ['5', '50', '105', '210', '525'],
    correctIndex: 2, // H
    correctLetter: 'H',
    explanation: 'Find LCM(15, 35). Prime factorizations: 15 = 3 × 5, 35 = 5 × 7. LCM = 3 × 5 × 7 = 105.',
    concepts: ['LCM', 'divisibility', 'prime factorization'],
    templateParams: { num1: 15, num2: 35 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 25
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-25',
    formId: '2176CPRE',
    questionNumber: 25,
    categories: ['G'],
    difficulty: 1,
    question: 'In △ABC shown below, the measure of ∠A is 58°, and AB ≅ AC. What is the measure of ∠C?',
    choices: ['32°', '42°', '58°', '61°', '62°'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'Isosceles triangle with AB = AC means base angles are equal: ∠B = ∠C. Sum of angles: 58 + ∠B + ∠C = 180. So 58 + 2∠C = 180. 2∠C = 122. ∠C = 61°.',
    concepts: ['isosceles triangle', 'triangle angle sum'],
    templateParams: { vertexAngle: 58 },
    hasFigure: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 26
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-26',
    formId: '2176CPRE',
    questionNumber: 26,
    categories: ['S', 'MDL'],
    difficulty: 2,
    question: 'About 1.48 × 10⁸ square kilometers of Earth\'s surface is land; the rest, about 3.63 × 10⁸ square kilometers, is water. If a returning space capsule lands at a random point on Earth\'s surface, which of the following is the best estimate of the probability that the space capsule will land in water?',
    choices: ['80%', '71%', '65%', '41%', '29%'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: 'Total surface = (1.48 + 3.63) × 10⁸ = 5.11 × 10⁸ km². P(water) = 3.63/5.11 ≈ 0.71 = 71%.',
    concepts: ['probability', 'scientific notation', 'proportions'],
    templateParams: { land: 1.48e8, water: 3.63e8 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 27
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-27',
    formId: '2176CPRE',
    questionNumber: 27,
    categories: ['S'],
    difficulty: 2,
    question: 'On the first 7 statistics tests of the semester, Jamal scored 61, 76, 79, 80, 80, 84, and 91. The mean, median, and mode of his scores were 79, 80, and 80, respectively. On the 8th statistics test, Jamal scored 90. How do the mean, median, and mode of all 8 of his scores compare to the mean, median, and mode of his first 7 scores?',
    choices: [
      'Mean: equal, Median: greater, Mode: greater',
      'Mean: greater, Median: greater, Mode: greater',
      'Mean: greater, Median: greater, Mode: equal',
      'Mean: greater, Median: equal, Mode: greater',
      'Mean: greater, Median: equal, Mode: equal'
    ],
    correctIndex: 4, // E
    correctLetter: 'E',
    explanation: 'Original mean = 79. New sum = 553 + 90 = 643. New mean = 643/8 = 80.375 > 79 (greater). Sorted 8 scores: 61, 76, 79, 80, 80, 84, 90, 91. Median = (80+80)/2 = 80 (equal). Mode = 80 (equal, still appears most).',
    concepts: ['mean', 'median', 'mode', 'effect of adding data'],
    templateParams: { scores: [61, 76, 79, 80, 80, 84, 91], newScore: 90 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 28
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-28',
    formId: '2176CPRE',
    questionNumber: 28,
    categories: ['G', 'MDL'],
    difficulty: 3,
    question: 'The solid rectangular prism shown below was built by alternating congruent black cubes and white cubes such that 2 cubes of the same color have at most 1 edge touching. What is the total number of white cubes that were used to build the prism?',
    choices: ['45', '102', '105', '140', '210'],
    correctIndex: 2, // H
    correctLetter: 'H',
    explanation: 'In a 3D checkerboard pattern, approximately half are each color. Total cubes in prism depends on dimensions. For a 7×6×5 prism = 210 cubes. Half = 105 white cubes.',
    concepts: ['3D geometry', 'counting', 'patterns'],
    templateParams: { dimensions: [7, 6, 5] },
    hasFigure: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 29
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-29',
    formId: '2176CPRE',
    questionNumber: 29,
    categories: ['G', 'MDL'],
    difficulty: 2,
    question: 'One side of square ABCD has a length of 12 meters. A certain rectangle whose area is equal to the area of ABCD has a width of 8 meters. What is the length, in meters, of the rectangle?',
    choices: ['12', '16', '18', '20', '24'],
    correctIndex: 2, // C
    correctLetter: 'C',
    explanation: 'Square area = 12² = 144 m². Rectangle area = width × length = 8 × L = 144. L = 144/8 = 18 meters.',
    concepts: ['area', 'rectangles', 'squares'],
    templateParams: { squareSide: 12, rectWidth: 8 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 30
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-30',
    formId: '2176CPRE',
    questionNumber: 30,
    categories: ['S'],
    difficulty: 2,
    question: 'The average of a list of 4 numbers is 92.0. A new list of 4 numbers has the same first 3 numbers as the original list, but the fourth number in the original list is 40, and the fourth number in the new list is 48. What is the average of this new list of numbers?',
    choices: ['81.0', '92.0', '94.0', '94.4', '96.6'],
    correctIndex: 2, // H
    correctLetter: 'H',
    explanation: 'Original sum = 4 × 92 = 368. New sum = 368 − 40 + 48 = 376. New average = 376/4 = 94.0.',
    concepts: ['average', 'effect of changing values'],
    templateParams: { count: 4, origAvg: 92, oldValue: 40, newValue: 48 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 31
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-31',
    formId: '2176CPRE',
    questionNumber: 31,
    categories: ['G', 'MDL'],
    difficulty: 2,
    question: 'The vector i represents 1 mile per hour east, and the vector j represents 1 mile per hour north. Maria is jogging south at 12 miles per hour. One of the following vectors represents Maria\'s velocity, in miles per hour. Which one?',
    choices: ['−12i', '−12j', '12i', '12j', '12i + 12j'],
    correctIndex: 1, // B
    correctLetter: 'B',
    explanation: 'South is the opposite direction of north. Since j represents north, south is −j. At 12 mph south: −12j.',
    concepts: ['vectors', 'direction'],
    templateParams: { speed: 12, direction: 'south' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 32
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-32',
    formId: '2176CPRE',
    questionNumber: 32,
    categories: ['N'],
    difficulty: 2,
    question: 'Four identical glasses are shown below. One glass is empty, and the other 3 glasses are 1/4 full, 1/2 full, and 4/5 full of water, respectively. If the water were redistributed equally among the 4 glasses, what fractional part of each glass would be filled?',
    choices: ['2/11', '8/11', '3/22', '31/60', '31/80'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'Total water = 0 + 1/4 + 1/2 + 4/5. Common denominator 20: 0 + 5/20 + 10/20 + 16/20 = 31/20. Divided by 4: (31/20)/4 = 31/80 per glass.',
    concepts: ['fractions', 'addition', 'division'],
    templateParams: { fractions: [0, '1/4', '1/2', '4/5'], containers: 4 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 33
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-33',
    formId: '2176CPRE',
    questionNumber: 33,
    categories: ['IES', 'MDL'],
    difficulty: 2,
    question: 'Aurelio is purchasing carpet tiles to cover an area of his living room floor that is 8⅓ feet wide by 10 feet long. Each carpet tile is a square 20 inches wide by 20 inches long. What is the minimum number of carpet tiles that Aurelio must purchase to cover this area of his living room floor?',
    choices: ['5', '11', '21', '30', '84'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'Convert feet to inches: 8⅓ ft = 100 inches, 10 ft = 120 inches. Tiles needed: (100/20) × (120/20) = 5 × 6 = 30 tiles.',
    concepts: ['unit conversion', 'area', 'tiling'],
    templateParams: { roomWidth: '8⅓', roomLength: 10, roomUnit: 'feet', tileSize: 20, tileUnit: 'inches' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 34
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-34',
    formId: '2176CPRE',
    questionNumber: 34,
    categories: ['G'],
    difficulty: 2,
    question: 'In the standard (x,y) coordinate plane, a circle with its center at (8,5) and a radius of 9 coordinate units has which of the following equations?',
    choices: [
      '(x − 8)² + (y − 5)² = 81',
      '(x − 8)² + (y − 5)² = 9',
      '(x + 8)² + (y + 5)² = 81',
      '(x + 8)² + (y + 5)² = 9',
      '(x + 5)² + (y + 8)² = 81'
    ],
    correctIndex: 0, // F
    correctLetter: 'F',
    explanation: 'Standard circle equation: (x − h)² + (y − k)² = r². Center (8,5), radius 9: (x − 8)² + (y − 5)² = 9² = 81.',
    concepts: ['circle equation', 'coordinate geometry'],
    templateParams: { center: [8, 5], radius: 9 }
  },

  // -------------------------------------------------------------------------
  // QUESTIONS 35-38 (Data set questions - Yq77 test)
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-35',
    formId: '2176CPRE',
    questionNumber: 35,
    categories: ['IES', 'MDL'],
    difficulty: 2,
    question: 'Many humans carry the gene Yq77. The Yq test determines with 100% accuracy whether a human carries Yq77. Sam designed a less expensive test called Sam77. Both tests were administered to 1,000 volunteers with these results:\n\n• Positive Sam77, Positive Yq: 590\n• Positive Sam77, Negative Yq: 10\n• Negative Sam77, Positive Yq: 25\n• Negative Sam77, Negative Yq: 375\n\nIt cost $2,500 to administer each Yq test and $50 to administer each Sam77 test. What was the total cost to administer both tests to all the volunteers?',
    choices: ['$1,537,500', '$1,556,750', '$1,568,250', '$2,500,000', '$2,550,000'],
    correctIndex: 4, // E
    correctLetter: 'E',
    explanation: 'Cost of Yq tests: 1000 × $2,500 = $2,500,000. Cost of Sam77 tests: 1000 × $50 = $50,000. Total: $2,550,000.',
    concepts: ['two-way tables', 'multiplication'],
    templateParams: { volunteers: 1000, yqCost: 2500, sam77Cost: 50 }
  },

  {
    id: '2176CPRE-36',
    formId: '2176CPRE',
    questionNumber: 36,
    categories: ['S'],
    difficulty: 2,
    question: '[Continuing from Q35 data] What percent of the volunteers actually carry Yq77?',
    choices: ['57.5%', '60.0%', '60.5%', '61.5%', '62.5%'],
    correctIndex: 3, // J
    correctLetter: 'J',
    explanation: 'Volunteers with Yq77 (Positive Yq test) = 590 + 25 = 615. Percent = 615/1000 = 61.5%.',
    concepts: ['percentages', 'two-way tables'],
    templateParams: { truePositive: 590, falseNegative: 25, total: 1000 }
  },

  {
    id: '2176CPRE-37',
    formId: '2176CPRE',
    questionNumber: 37,
    categories: ['S', 'MDL'],
    difficulty: 2,
    question: '[Continuing from Q35 data] For how many volunteers did the Sam77 test give an incorrect result?',
    choices: ['10', '25', '35', '385', '400'],
    correctIndex: 2, // C
    correctLetter: 'C',
    explanation: 'Incorrect Sam77 results = false positives + false negatives = 10 + 25 = 35.',
    concepts: ['two-way tables', 'false positive', 'false negative'],
    templateParams: { falsePositive: 10, falseNegative: 25 }
  },

  {
    id: '2176CPRE-38',
    formId: '2176CPRE',
    questionNumber: 38,
    categories: ['S', 'MDL'],
    difficulty: 2,
    question: '[Continuing from Q35 data] One of the volunteers whose Sam77 test result was positive will be chosen at random. To the nearest 0.001, what is the probability the chosen volunteer does NOT possess Yq77?',
    choices: ['0.017', '0.026', '0.035', '0.041', '0.063'],
    correctIndex: 0, // F
    correctLetter: 'F',
    explanation: 'Positive Sam77: 590 + 10 = 600. Of these, 10 don\'t have Yq77. P = 10/600 = 0.0167 ≈ 0.017.',
    concepts: ['conditional probability', 'two-way tables'],
    templateParams: { positiveSam77WithYq77: 590, positiveSam77WithoutYq77: 10 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 39
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-39',
    formId: '2176CPRE',
    questionNumber: 39,
    categories: ['N'],
    difficulty: 2,
    question: 'Given matrices X = [−1  0] (1×2) and Y = [−2; −1] (2×1 column vector), which of the following matrices is XY?',
    choices: ['[−4]', '[−3]', '[−2]', '[2]', '[3]'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'XY = [−1  0] × [−2; −1] = (−1)(−2) + (0)(−1) = 2 + 0 = [2].',
    concepts: ['matrix multiplication'],
    templateParams: { X: [[-1, 0]], Y: [[-2], [-1]] }
  },

  // -------------------------------------------------------------------------
  // QUESTION 40
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-40',
    formId: '2176CPRE',
    questionNumber: 40,
    categories: ['G'],
    difficulty: 2,
    question: 'Regardless of how the graph is oriented in the standard (x,y) coordinate plane, NO graph in one of the following categories has a vertical line of symmetry. Which one?',
    choices: ['Line', 'Square', 'Pentagon', 'Parallelogram', 'Scalene triangle'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'A scalene triangle has no lines of symmetry at all (all sides different lengths), so it cannot have a vertical line of symmetry regardless of orientation.',
    concepts: ['symmetry', 'geometric shapes'],
    templateParams: {}
  },

  // -------------------------------------------------------------------------
  // QUESTION 41
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-41',
    formId: '2176CPRE',
    questionNumber: 41,
    categories: ['A'],
    difficulty: 2,
    question: 'The equation 24x² + 2x = 15 has 2 solutions. What is the greater of the 2 solutions?',
    choices: ['3/4', '4/3', '5/6', '7/6', '11/15'],
    correctIndex: 0, // A
    correctLetter: 'A',
    explanation: '24x² + 2x − 15 = 0. Factor: (4x − 3)(6x + 5) = 0. Solutions: x = 3/4 or x = −5/6. The greater solution is 3/4.',
    concepts: ['quadratic equations', 'factoring'],
    templateParams: { a: 24, b: 2, c: -15 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 42
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-42',
    formId: '2176CPRE',
    questionNumber: 42,
    categories: ['F'],
    difficulty: 3,
    question: 'Which of the following expressions is equal to (sin 60°)(cos 30°) + (cos 60°)(sin 30°)?',
    choices: ['cos(60° − 30°)', 'cos(60° + 30°)', 'sin(60° − 30°)', 'sin(60° + 30°)', 'sin((60° + 30°)/2)'],
    correctIndex: 3, // J
    correctLetter: 'J',
    explanation: 'This matches the sine addition formula: sin(A + B) = sin A cos B + cos A sin B. So this equals sin(60° + 30°) = sin(90°) = 1.',
    concepts: ['trigonometric identities', 'angle addition formulas'],
    templateParams: { angle1: 60, angle2: 30 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 43
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-43',
    formId: '2176CPRE',
    questionNumber: 43,
    categories: ['G'],
    difficulty: 2,
    question: 'What is the area, in square units, of a circle that has a circumference 12π units long?',
    choices: ['6π', '12π', '24π', '36π', '144π'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'Circumference = 2πr = 12π, so r = 6. Area = πr² = π(6)² = 36π.',
    concepts: ['circle circumference', 'circle area'],
    templateParams: { circumference: '12π' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 44
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-44',
    formId: '2176CPRE',
    questionNumber: 44,
    categories: ['A', 'MDL'],
    difficulty: 3,
    question: 'A barrel contains 25 liters of a solvent mixture that is 40% solvent and 60% water. Lee will add pure solvent to the barrel, without removing any of the mixture currently in the barrel, so that the new mixture will contain 50% solvent and 50% water. How many liters of pure solvent should Lee add to create this new mixture?',
    choices: ['2.5', '5', '10', '12.5', '15'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: 'Current solvent = 0.40 × 25 = 10 L. After adding x liters of pure solvent: (10 + x)/(25 + x) = 0.50. So 10 + x = 12.5 + 0.5x. 0.5x = 2.5. x = 5 liters.',
    concepts: ['mixture problems', 'concentration'],
    templateParams: { initialVolume: 25, initialConcentration: 0.40, targetConcentration: 0.50 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 45
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-45',
    formId: '2176CPRE',
    questionNumber: 45,
    categories: ['A'],
    difficulty: 2,
    question: 'For all x ≠ ±y, x/(x+y) + y/(x−y) = ?',
    choices: ['1/(x−y)', '(x+y)/(x−y)', '(x+y)/(2x)', 'x² + y²', '(x² + y²)/(x² − y²)'],
    correctIndex: 4, // E
    correctLetter: 'E',
    explanation: 'Common denominator: (x+y)(x−y) = x² − y². Numerator: x(x−y) + y(x+y) = x² − xy + xy + y² = x² + y². Result: (x² + y²)/(x² − y²).',
    concepts: ['rational expressions', 'adding fractions'],
    templateParams: {}
  },

  // -------------------------------------------------------------------------
  // QUESTION 46
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-46',
    formId: '2176CPRE',
    questionNumber: 46,
    categories: ['A', 'MDL'],
    difficulty: 2,
    question: 'Mary, James, and Carlos sold ¼-page advertisements for the school yearbook. Mary sold twice as many as Carlos did, and James sold 3 times as many as Mary did. What fraction of these advertisements did Carlos sell?',
    choices: ['1/9', '1/7', '1/6', '1/5', '1/3'],
    correctIndex: 0, // F
    correctLetter: 'F',
    explanation: 'Let Carlos sell c ads. Mary = 2c. James = 3(2c) = 6c. Total = c + 2c + 6c = 9c. Carlos\'s fraction = c/(9c) = 1/9.',
    concepts: ['ratios', 'algebraic reasoning'],
    templateParams: { carlosMultiple: 1, maryMultiple: 2, jamesMultiple: 6 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 47
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-47',
    formId: '2176CPRE',
    questionNumber: 47,
    categories: ['S', 'MDL'],
    difficulty: 2,
    question: 'In a window display at a flower shop, there are 3 spots for 1 plant each. To fill these 3 spots, Emily has 6 plants to select from, each of a different type. Selecting from the 6 plants, Emily can make how many possible display arrangements with 1 plant in each spot?',
    choices: ['3', '6', '15', '120', '216'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'This is a permutation since order (position) matters. P(6,3) = 6!/(6−3)! = 6 × 5 × 4 = 120.',
    concepts: ['permutations', 'counting'],
    templateParams: { n: 6, r: 3 }
  },

  // -------------------------------------------------------------------------
  // QUESTIONS 48-50 (Graph questions - Triangle MPQ)
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-48',
    formId: '2176CPRE',
    questionNumber: 48,
    categories: ['G'],
    difficulty: 3,
    question: 'The quadratic function f and △MPQ are graphed in the standard (x,y) coordinate plane. Points M(2a, 5b), N(4a, 9b), and P(6a, 5b) are on f. Point Q(4a, 0) is NOT on f. In terms of a and b, what is the area, in square coordinate units, of △MPQ?',
    choices: ['8ab', '10ab', '12ab', '15ab', '20ab'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: 'Base MP = 6a − 2a = 4a (horizontal). Height = 5b − 0 = 5b (from Q to line MP). Area = ½ × base × height = ½ × 4a × 5b = 10ab.',
    concepts: ['triangle area', 'coordinate geometry'],
    templateParams: { M: ['2a', '5b'], P: ['6a', '5b'], Q: ['4a', 0] },
    hasFigure: true
  },

  {
    id: '2176CPRE-49',
    formId: '2176CPRE',
    questionNumber: 49,
    categories: ['F'],
    difficulty: 3,
    question: '[Continuing from Q48] Point M will remain fixed, and point Q will move to the right along the x-axis. As Q continues to move to the right, which of the following statements describes what will happen to the slope of MQ?',
    choices: [
      'It will decrease and eventually be negative.',
      'It will decrease but never be negative.',
      'It will stay the same.',
      'It will increase but never be positive.',
      'It will increase and eventually be positive.'
    ],
    correctIndex: 1, // D (actually B based on reasoning)
    correctLetter: 'D',
    explanation: 'M is at (2a, 5b). Q starts at (4a, 0) and moves right. Slope = (5b − 0)/(2a − x_Q). Initially Q is right of M, so slope is negative. As Q moves further right, |denominator| increases, making |slope| smaller (approaching 0 from negative side), but stays negative.',
    concepts: ['slope', 'function behavior'],
    templateParams: {}
  },

  {
    id: '2176CPRE-50',
    formId: '2176CPRE',
    questionNumber: 50,
    categories: ['F'],
    difficulty: 3,
    question: '[Continuing from Q48] One of the following values is equal to f(5a). Which one?',
    choices: ['3a', '5a', '5b', '8a', '8b'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'The parabola passes through M(2a, 5b), N(4a, 9b) (vertex), and P(6a, 5b). By symmetry about x = 4a, f(5a) = f(3a). Since 3a is 1a from vertex and 5a is 1a from vertex on other side, both give same y. We can see f(2a) = f(6a) = 5b. By the parabola pattern, f(5a) = 8b.',
    concepts: ['quadratic symmetry', 'function evaluation'],
    templateParams: {}
  },

  // -------------------------------------------------------------------------
  // QUESTION 51
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-51',
    formId: '2176CPRE',
    questionNumber: 51,
    categories: ['S', 'MDL'],
    difficulty: 3,
    question: 'Twelve jurors are needed for an upcoming trial. The 12 jurors will be selected from a jury pool of about 60 people. The court records show a trend that only 40% of the people who are summoned for jury duty actually appear and that of the people who appear, 1/3 are excused. If this same trend continues, how many people should be summoned to have as close as possible to 60 people in the jury pool?',
    choices: ['45', '90', '150', '225', '800'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'Let x = people summoned. 40% appear: 0.40x. Of those, 2/3 remain (1/3 excused): (2/3)(0.40x) = 60. So (0.80/3)x = 60. x = 60 × 3/0.80 = 225.',
    concepts: ['compound percentages', 'working backwards'],
    templateParams: { target: 60, appearRate: 0.40, excuseRate: '1/3' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 52
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-52',
    formId: '2176CPRE',
    questionNumber: 52,
    categories: ['N'],
    difficulty: 2,
    question: 'What is the 275th digit after the decimal point in the repeating decimal 0.6295̄ (where 6295 repeats)?',
    choices: ['0', '2', '5', '6', '9'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'The pattern "6295" repeats every 4 digits. 275 ÷ 4 = 68 remainder 3. The 3rd digit in "6295" is 9.',
    concepts: ['repeating decimals', 'modular arithmetic'],
    templateParams: { pattern: '6295', position: 275 }
  },

  // -------------------------------------------------------------------------
  // QUESTION 53
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-53',
    formId: '2176CPRE',
    questionNumber: 53,
    categories: ['F'],
    difficulty: 3,
    question: 'Given that f(x) = x² − 4 and g(x) = x + 3, what are all the values of x for which f(g(x)) = 0?',
    choices: ['−5 and −1', '−3, −2, and 2', '−1 and 1', '1 and 5', '−√5 and √5'],
    correctIndex: 0, // A
    correctLetter: 'A',
    explanation: 'f(g(x)) = f(x + 3) = (x + 3)² − 4 = 0. (x + 3)² = 4. x + 3 = ±2. x = −3 + 2 = −1 or x = −3 − 2 = −5.',
    concepts: ['composite functions', 'solving equations'],
    templateParams: { f: 'x² − 4', g: 'x + 3' }
  },

  // -------------------------------------------------------------------------
  // QUESTION 54
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-54',
    formId: '2176CPRE',
    questionNumber: 54,
    categories: ['N'],
    difficulty: 3,
    question: 'Given that p is a positive number, n is a negative number, and |p| > |n|, which of the following expressions has the greatest value?',
    choices: ['|(p−n)/p|', '|(p−n)/n|', '|(p+n)/(p−n)|', '|(p+n)/p|', '|(p+n)/n|'],
    correctIndex: 1, // G
    correctLetter: 'G',
    explanation: 'Since p > 0, n < 0, and |p| > |n|: p − n = p + |n| > 0 (positive). Dividing by n (negative) gives most negative, but absolute value makes it largest. |(p−n)/n| = (p+|n|)/|n| which is > 1 + p/|n|, the largest.',
    concepts: ['absolute value', 'algebraic reasoning'],
    templateParams: {}
  },

  // -------------------------------------------------------------------------
  // QUESTION 55
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-55',
    formId: '2176CPRE',
    questionNumber: 55,
    categories: ['N'],
    difficulty: 3,
    question: 'If i = √(−1), then (i + i² + i³)/(i³ + i⁴ + i⁵) = ?',
    choices: ['−3', '−1', '1/2', '1', '3'],
    correctIndex: 1, // B
    correctLetter: 'B',
    explanation: 'i¹ = i, i² = −1, i³ = −i, i⁴ = 1, i⁵ = i. Numerator: i + (−1) + (−i) = −1. Denominator: (−i) + 1 + i = 1. Result: −1/1 = −1.',
    concepts: ['complex numbers', 'powers of i'],
    templateParams: { numPowers: [1, 2, 3], denPowers: [3, 4, 5] }
  },

  // -------------------------------------------------------------------------
  // QUESTION 56
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-56',
    formId: '2176CPRE',
    questionNumber: 56,
    categories: ['A'],
    difficulty: 3,
    question: 'In one of the following graphs in the standard (x,y) coordinate plane, the solution set to the system of inequalities below is shown shaded. Which one?\n\nx + 2y ≤ 6\n3x² > 12 − 3y²',
    choices: ['F', 'G', 'H', 'J', 'K'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'First inequality: x + 2y ≤ 6 is a half-plane below/on the line x + 2y = 6. Second: 3x² + 3y² > 12 → x² + y² > 4, which is outside a circle of radius 2. The shaded region is the intersection: outside the circle AND below/on the line.',
    concepts: ['systems of inequalities', 'linear and quadratic inequalities'],
    templateParams: {},
    hasFigure: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 57
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-57',
    formId: '2176CPRE',
    questionNumber: 57,
    categories: ['A'],
    difficulty: 3,
    question: 'Let a, b, c, and d be real numbers. Given that ac = 1, (b+c)/d is undefined, and abc = d, which of the following must be true?',
    choices: ['a = 0 or c = 0', 'a = 1 and c = 1', 'a = −c', 'b = 0', 'b + c = 0'],
    correctIndex: 3, // D
    correctLetter: 'D',
    explanation: 'Since ac = 1, neither a nor c is 0. Since (b+c)/d is undefined, d = 0. Since abc = d = 0 and ac ≠ 0, we must have b = 0.',
    concepts: ['algebraic reasoning', 'undefined expressions'],
    templateParams: {}
  },

  // -------------------------------------------------------------------------
  // QUESTION 58
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-58',
    formId: '2176CPRE',
    questionNumber: 58,
    categories: ['F'],
    difficulty: 3,
    question: 'A cosine function is shown in the standard (x,y) coordinate plane below. The amplitude is 3 and the period is π. One of the following equations represents this function. Which one?',
    choices: ['y = 2cos(x/3)', 'y = 2cos(3x)', 'y = 3cos(x/3)', 'y = 3cos(x/2)', 'y = 3cos(2x)'],
    correctIndex: 4, // K
    correctLetter: 'K',
    explanation: 'Amplitude = 3 (coefficient of cosine). Period = π = 2π/b, so b = 2. Equation: y = 3cos(2x).',
    concepts: ['trigonometric functions', 'amplitude', 'period'],
    templateParams: { amplitude: 3, period: 'π' },
    hasFigure: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 59
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-59',
    formId: '2176CPRE',
    questionNumber: 59,
    categories: ['G', 'MDL'],
    difficulty: 3,
    question: 'The figure below shows a flying kite. At a certain moment, the kite string forms an angle of elevation of 75° from point A on the ground. At the same moment, the angle of elevation of the kite at point B, 240 ft from A on level ground, is 45°. What is the length, in feet, of the string?',
    choices: ['60√3', '80√6', '144', '180', '240'],
    correctIndex: 1, // B
    correctLetter: 'B',
    explanation: 'Using law of sines. Angle at kite = 180° − 75° − 45° = 60°. By law of sines: string/sin(45°) = 240/sin(60°). String = 240 × (√2/2)/(√3/2) = 240 × √2/√3 = 240√2/√3 = 80√6.',
    concepts: ['law of sines', 'trigonometry', 'angles of elevation'],
    templateParams: { angleA: 75, angleB: 45, distance: 240 },
    hasFigure: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 60
  // -------------------------------------------------------------------------
  {
    id: '2176CPRE-60',
    formId: '2176CPRE',
    questionNumber: 60,
    categories: ['A', 'MDL'],
    difficulty: 2,
    question: 'If a publisher charges $15 for the first copy of a book that is ordered and $12 for each additional copy, which of the following expressions represents the cost of y books?',
    choices: ['12y + 3', '12y + 15', '15y − 3', '15y + 3', '15y + 12'],
    correctIndex: 0, // F
    correctLetter: 'F',
    explanation: 'First book: $15. Additional (y−1) books: $12 each. Total = 15 + 12(y−1) = 15 + 12y − 12 = 12y + 3.',
    concepts: ['linear expressions', 'cost functions'],
    templateParams: { firstCopy: 15, additionalCopy: 12 }
  }
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getQuestionsByCategory(questions, categoryCode) {
  return questions.filter(q => q.categories.includes(categoryCode));
}

export function getQuestionsByDifficulty(questions, level) {
  return questions.filter(q => q.difficulty === level);
}

export function getQuestionsByForm(questions, formId) {
  return questions.filter(q => q.formId === formId);
}

// Weighted random selection favoring weak categories
export function selectAdaptiveQuestions(allQuestions, performance, count = 10) {
  const weaknessScores = {};
  
  // Calculate weakness for each category
  Object.keys(ACT_CATEGORIES).forEach(cat => {
    const perf = performance[cat] || { correct: 0, total: 0 };
    if (perf.total === 0) {
      weaknessScores[cat] = 0.5; // Neutral for untested
    } else {
      weaknessScores[cat] = 1 - (perf.correct / perf.total);
    }
  });
  
  const selected = [];
  const usedIds = new Set();
  
  for (let i = 0; i < count && selected.length < count; i++) {
    // Calculate total weight
    const available = allQuestions.filter(q => !usedIds.has(q.id));
    if (available.length === 0) break;
    
    // Score each question by its category weakness
    const scored = available.map(q => {
      const maxWeakness = Math.max(...q.categories.map(c => weaknessScores[c] || 0.5));
      return { question: q, score: maxWeakness + Math.random() * 0.3 };
    });
    
    // Sort by score (descending) and pick top
    scored.sort((a, b) => b.score - a.score);
    const pick = scored[0].question;
    
    selected.push(pick);
    usedIds.add(pick.id);
  }
  
  // Shuffle to avoid predictable ordering
  for (let i = selected.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [selected[i], selected[j]] = [selected[j], selected[i]];
  }
  
  return selected;
}

// =============================================================================
// EXPORT ALL
// =============================================================================

export default {
  categories: ACT_CATEGORIES,
  scaleTable: SCALE_SCORE_TABLE,
  getScaleScore,
  questions: questions_2176CPRE,
  getQuestionsByCategory,
  getQuestionsByDifficulty,
  getQuestionsByForm,
  selectAdaptiveQuestions
};
